'use client';

import { createContext, type ReactNode, useContext, useMemo } from 'react';
import type { Market } from '../config/domain-locale-map';
import type { MarketMetadata } from '../market';

interface MarketContextType {
  market: Market;
  marketMetadata: MarketMetadata;
  apiBaseUrl: string;
}

const MarketContext = createContext<MarketContextType | undefined>(undefined);

interface MarketProviderProps extends MarketContextType {
  children: ReactNode;
}

export const MarketProvider = ({
  market,
  marketMetadata,
  apiBaseUrl,
  children,
}: MarketProviderProps) => {
  const value = useMemo(
    () => ({ market, marketMetadata, apiBaseUrl }),
    [market, marketMetadata, apiBaseUrl],
  );

  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
};

const useMarketSettings = (): MarketContextType => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarketSettings must be used within a MarketProvider');
  }
  return context;
};

export const useMarket = () => useMarketSettings().market;

export const useMarketMetadata = () => useMarketSettings().marketMetadata;

export const useApiBaseUrl = () => {
  const { apiBaseUrl } = useMarketSettings();

  return apiBaseUrl;
};
